import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import {
  ICommentFieldsFragment,
  ICommentInput,
  IConversationThreadFieldsFragment,
} from '../../../../../utils/apollo/graphqlSchema';
import ConversationInput from '../../../ConversationInput';
import useUpdateThreadComment from '../../../hooks/useUpdateThreadComment';

interface IProps {
  thread: IConversationThreadFieldsFragment;
  comment: ICommentFieldsFragment;
  onCancel: () => void;
}

const Container = styled.div`
  flex: 1;
  width: 100%;
`;

const EntityConversationThreadCommentInput = ({
  thread,
  comment,
  onCancel,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const { id, attachedImageEmbed, message } = comment;
  const htmlMessage = message?.html;

  const updateComment = useUpdateThreadComment({
    entity: thread.entity,
    threadId: thread.id,
    commentId: id,
  });

  const handleSubmitUpdatedComment = async (commentInput: ICommentInput) => {
    // If nothing changed - cancel editing on submit
    const isPristine =
      isEqual(htmlMessage, commentInput.message?.html) &&
      isEqual(
        attachedImageEmbed?.id ?? null,
        commentInput.attachedImageEmbedId,
      );

    if (!isPristine) {
      await updateComment(commentInput);
    }

    onCancel();
  };

  // Scroll component into view to see comment and footer with actions
  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, []);

  return (
    <Container ref={ref}>
      <ConversationInput
        onSubmit={handleSubmitUpdatedComment}
        onCancel={onCancel}
        participants={thread.participants}
        htmlMessage={htmlMessage || null}
        attachedImageEmbed={attachedImageEmbed}
        isUpdate={true}
      />
    </Container>
  );
};

export default EntityConversationThreadCommentInput;
